import { useState } from 'react';
import { useApiDBTM } from 'pages/content/api/api-dbtm';
import { useApiGeneric } from 'pages/content/api/api-generic';
import useTasksStore from 'stores/tasksStore';

export const useAPITodoList = () => {
    const { fetchAPI, LoadingAnimationSimple } = useApiDBTM();
    const { fetchAPI: fetchGenericAPI, LoadingAnimation: GenericLoadingAnimation } = useApiGeneric();
    const [isLoading, setIsLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("Cargando tareas...");
    
    // Zustand store actions
    const { 
        setTasks, 
        setLoading, 
        deleteCompletedTasks, 
        shouldFetch,
        getTasksForUser 
    } = useTasksStore();

    // Load todos for a specific user
    const loadTodos = async (usuarioId, OnLoaded, forceFetch = false) => {
        // Check if we should fetch from API or use cached data (unless forcing fetch)
        if (!forceFetch && !shouldFetch()) {
            const userTasks = getTasksForUser(usuarioId);
            OnLoaded(true, userTasks);
            return;
        }

        setIsLoading(true);
        setLoading(true);
        setLoadingText("Cargando tareas...");

        await fetchAPI({
            section: 'app_todo_list',
            action: 'GetTable',
            params: {
                filters: [
                {
                    columnId: 'usuario_id',
                    value: usuarioId || '',
                    type: 'number',
                }
                ]
            },
            OnLoaded: (success, payload) => {
                setIsLoading(false);
                setLoading(false);

                if (success && payload.tableData) {
                    // Sort tasks by ID in descending order (newer tasks first)
                    const sortedTasks = [...payload.tableData].sort((a, b) => {
                        return parseInt(b.id) - parseInt(a.id);
                    });

                    // Store sorted tasks
                    setTasks(sortedTasks);

                    // Return sorted API data
                    OnLoaded(true, sortedTasks);
                } else {
                    OnLoaded(false, []);
                }
            }
        });
    };

    // Add a new todo
    const addTodo = async (usuarioId, message, OnLoaded) => {
        if (!message.trim()) {
            OnLoaded(false, null);
            return;
        }

        setIsLoading(true);
        setLoading(true);
        setLoadingText("Agregando tarea...");

        // DBTM SaveRow contract: `mode` plus a `data` object with the row columns
        await fetchAPI({
            section: 'app_todo_list',
            action: 'SaveRow',
            params: {
                mode: 'NEW',
                data: {
                    texto: message.trim(),
                    usuario_id: usuarioId,
                    estado: 'pendiente', // New todos start as not completed
                    prioridad: 'Media',
                    // `fecha` is NOT NULL and has no SQL default in app_todo_list
                    fecha: new Date().toISOString().slice(0, 10)
                }
            },
            OnLoaded: (success, payload) => {
                setIsLoading(false);
                setLoading(false);

                if (success) {
                    // Force reload all tasks to ensure the new task appears
                    loadTodos(usuarioId, () => {}, true); // Force fresh fetch
                }

                OnLoaded(success, payload);
            }
        });
    };

    // Toggle todo completion status
    const toggleTodo = async (todo, usuarioId, OnLoaded) => {
        setIsLoading(true);
        setLoading(true);
        setLoadingText("Actualizando tarea...");

        // DBTM SaveRow contract: `mode` plus a `data` object with the row columns
        await fetchAPI({
            section: 'app_todo_list',
            action: 'SaveRow',
            params: {
                mode: 'EDIT',
                data: {
                    id: todo.id,
                    texto: todo.texto,
                    usuario_id: usuarioId,
                    estado: todo.estado === 'completado' ? 'pendiente' : 'completado' // Toggle status
                }
            },
            OnLoaded: (success, payload) => {
                setIsLoading(false);
                setLoading(false);

                if (success) {
                    // Force reload tasks to ensure UI reflects the actual database state
                    loadTodos(usuarioId, () => {}, true); // Force fresh fetch
                }

                OnLoaded(success, payload);
            }
        });
    };

    // Delete all completed todos for a user
    const deleteCompleted = async (usuarioId, OnLoaded) => {
        setIsLoading(true);
        setLoading(true);
        setLoadingText("Eliminando tareas completadas...");

        // Dedicated module endpoint (not part of the generic DBTM actions)
        await fetchGenericAPI({
            section: 'app_todo_list',
            action: 'DeleteCompleted',
            params: {
                usuario_id: usuarioId // Use 'usuario_id' instead of 'usuarioId'
            },
            OnLoaded: (success, payload) => {
                setIsLoading(false);
                setLoading(false);

                if (success) {
                    // Update store by removing completed tasks
                    deleteCompletedTasks();
                }

                OnLoaded(success, payload);
            }
        });
    };

    // Each loading animation self-gates on the hook instance that fired the
    // request, so both instances must be rendered.
    const TodoLoadingAnimation = () => {
        return (
            <>
                <LoadingAnimationSimple />
                <GenericLoadingAnimation />
            </>
        );
    };

    return {
        loadTodos,
        addTodo,
        toggleTodo,
        deleteCompleted,
        TodoLoadingAnimation,
        isLoading,
        loadingText
    };
};

